import {
  type InfiniteData,
  type QueryKey,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import type { FundGridPage, FundListDetail, FundSummary } from '@nemo/api';
import { useToast } from '../ui/Toast.js';
import { useTRPC } from '../trpc.js';
import { type FundLists, type FundWrite, applyWriteLocally } from './fundWrites.js';

export interface FundWriter {
  /** Applies the write to the grid at once, then to the table, and rolls the
   * grid back with a message if the table refuses. */
  apply: (fundId: string, write: FundWrite) => Promise<void>;
  applyMany: (writes: readonly { fundId: string; write: FundWrite }[]) => Promise<void>;
  isPending: boolean;
}

/** Every cached answer a write touched, as it was, to put back if the table refuses. */
type Snapshot = readonly (readonly [QueryKey, unknown])[];

export function useFundWriter(lists: FundLists): FundWriter {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const toast = useToast();

  const update = useMutation(trpc.funds.update.mutationOptions());
  const updateMany = useMutation(trpc.funds.updateMany.mutationOptions());
  const setSectors = useMutation(trpc.funds.setSectors.mutationOptions());
  const setOwners = useMutation(trpc.funds.setOwners.mutationOptions());
  const setStages = useMutation(trpc.funds.setStages.mutationOptions());

  const held = [
    trpc.funds.get.pathFilter(),
    trpc.funds.page.infiniteQueryFilter(),
    trpc.lists.get.pathFilter(),
  ];

  /** A fund can be in the grid's pages, on a list and on its own, all at once. */
  function mapHeld(change: (fund: FundSummary) => FundSummary): void {
    // The fund on its own also carries its rounds, which a written row does not.
    queryClient.setQueriesData(
      trpc.funds.get.pathFilter(),
      (current: FundSummary | null | undefined) =>
        current === undefined || current === null ? current : { ...current, ...change(current) },
    );
    queryClient.setQueriesData(
      trpc.lists.get.pathFilter(),
      (current: FundListDetail | undefined) =>
        current === undefined
          ? undefined
          : {
              ...current,
              items: current.items.map((item) => ({ ...item, fund: change(item.fund) })),
            },
    );
    queryClient.setQueriesData(
      trpc.funds.page.infiniteQueryFilter(),
      (current: InfiniteData<FundGridPage, string | null> | undefined) =>
        current === undefined
          ? undefined
          : {
              ...current,
              pages: current.pages.map((page) => ({ ...page, funds: page.funds.map(change) })),
            },
    );
  }

  function optimistically(writes: readonly { fundId: string; write: FundWrite }[]): Snapshot {
    const snapshot = held.flatMap((filter) => queryClient.getQueriesData(filter));
    mapHeld((fund) => {
      const write = writes.find((entry) => entry.fundId === fund.id);
      return write === undefined ? fund : applyWriteLocally(fund, write.write, lists);
    });
    return snapshot;
  }

  function reconcile(rows: readonly (FundSummary | null)[]): void {
    mapHeld((fund) => rows.find((row) => row?.id === fund.id) ?? fund);
  }

  function restore(snapshot: Snapshot, error: unknown): void {
    for (const [key, data] of snapshot) queryClient.setQueryData(key, data);
    toast(error instanceof Error ? error.message : 'That edit did not save.');
  }

  async function send(fundId: string, write: FundWrite): Promise<FundSummary | null> {
    switch (write.kind) {
      case 'edits': {
        const edits = write.edits.map((edit) => ({ id: fundId, ...edit }));
        const [first] = edits;
        if (first === undefined) return null;
        if (edits.length === 1) return update.mutateAsync(first);
        const rows = await updateMany.mutateAsync({ edits });
        return rows[0] ?? null;
      }
      case 'sectors':
        return setSectors.mutateAsync({ id: fundId, sectorIds: [...write.sectorIds] });
      case 'owners':
        return setOwners.mutateAsync({ id: fundId, memberIds: [...write.memberIds] });
      case 'stages':
        return setStages.mutateAsync({ id: fundId, stages: [...write.stages] });
    }
  }

  return {
    isPending:
      update.isPending ||
      updateMany.isPending ||
      setSectors.isPending ||
      setOwners.isPending ||
      setStages.isPending,

    apply: async (fundId, write) => {
      const snapshot = optimistically([{ fundId, write }]);
      try {
        reconcile([await send(fundId, write)]);
      } catch (error) {
        restore(snapshot, error);
        throw error;
      }
    },

    applyMany: async (writes) => {
      const snapshot = optimistically(writes);
      try {
        // A block paste is one transaction, so every column edit in it travels
        // together and a bad cell leaves the table untouched.
        const edits = writes.flatMap(({ fundId, write }) =>
          write.kind === 'edits' ? write.edits.map((edit) => ({ id: fundId, ...edit })) : [],
        );
        const joins = writes.filter((entry) => entry.write.kind !== 'edits');
        const rows = edits.length === 0 ? [] : await updateMany.mutateAsync({ edits });
        const joined = await Promise.all(joins.map(({ fundId, write }) => send(fundId, write)));
        reconcile([...rows, ...joined]);
      } catch (error) {
        restore(snapshot, error);
        throw error;
      }
    },
  };
}
